import { sampleQueryKeys, studyQueryKeys } from '@/config/queryKeys';
import { apiClient, type DownloadedFile } from '@/lib/api-client';
import type {
  SampleFilters,
  SampleQcSummary,
  SampleSortField,
  SampleWithNamesPublic,
  PaginatedSamplesResponse,
  SampleSearchRequest,
  SortOrder,
} from '@/types';

/**
 * Thrown when a sample is reachable only by navigating from the samples list, because no
 * single-sample retrieval exists on the API.
 */
export class SampleDetailUnavailableError extends Error {
  constructor(sampleId: string) {
    super(`Sample ${sampleId} cannot be fetched on its own; open it from the samples list`);
    this.name = 'SampleDetailUnavailableError';
  }
}

/**
 * Fetch a single sample by ID — currently always rejects.
 *
 * There is no `GET /orgs/{org_id}/samples/{sample_id}`, and `OrgSampleFilters` declares
 * `additionalProperties: false`, so the org search endpoint rejects an `id` filter with
 * `422 extra_forbidden`. Nothing in the contract identifies one sample, so this rejects without
 * issuing a request the API is guaranteed to refuse. Restore a real fetch here once the API
 * exposes single-sample retrieval (by-id GET, or an `id` field on `OrgSampleFilters`).
 */
export function getSample(sampleId: string): Promise<SampleWithNamesPublic> {
  return Promise.reject(new SampleDetailUnavailableError(sampleId));
}

export async function searchStudySamples(
  orgId: string,
  studyId: string,
  request: SampleSearchRequest,
): Promise<PaginatedSamplesResponse> {
  const result = await apiClient.post<PaginatedSamplesResponse>(
    studyQueryKeys.endpoints.samplesSearch(orgId, studyId),
    request,
    { expectJson: true },
  );
  if (result === null) throw new Error('searchStudySamples: endpoint returned no content');
  return result;
}

/**
 * Org-wide QC tallies. Deliberately independent of the samples grid's filter state — the
 * backend counts every sample in scope, not the current page.
 */
export async function fetchSamplesQcSummary(orgId: string): Promise<SampleQcSummary> {
  const result = await apiClient.get<SampleQcSummary>(sampleQueryKeys.endpoints.qcSummary(orgId), {
    expectJson: true,
  });

  if (result === null) throw new Error('fetchSamplesQcSummary: endpoint returned no content');
  return result;
}

export interface StudySamplesDownloadOptions {
  sortBy?: SampleSortField;
  sortOrder?: SortOrder;
  filters?: SampleFilters | null;
}

/** Streams a study's full sample list as CSV. */
export async function downloadStudySamplesCsv(
  orgId: string,
  studyId: string,
  options: StudySamplesDownloadOptions = {},
): Promise<DownloadedFile> {
  const { filters, sortBy, sortOrder } = options;

  return apiClient.download(studyQueryKeys.endpoints.samplesDownload(orgId, studyId), {
    query: {
      sort_by: sortBy,
      sort_order: sortOrder,
      // The endpoint takes `filters` as a JSON-encoded query param, not a body.
      filters: filters ? JSON.stringify(filters) : undefined,
    },
  });
}
